import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { paymentsService } from "../services/payment.service";

export const fetchPayments = createAsyncThunk(
  "payment/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await paymentsService.getPayments();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch payments.");
    }
  }
);

export const fetchActivePayments = createAsyncThunk(
  "payment/fetchActive",
  async (_, thunkAPI) => {
    try {
      return await paymentsService.getActivePayments();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch active payments.");
    }
  }
);

export const fetchInactivePayments = createAsyncThunk(
  "payment/fetchInactive",
  async (_, thunkAPI) => {
    try {
      return await paymentsService.getInactivePayments();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch inactive payments.");
    }
  }
);

export const fetchPaymentById = createAsyncThunk(
  "payment/fetchById",
  async (id, thunkAPI) => {
    try {
      return await paymentsService.getPaymentById(id);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch payment.");
    }
  }
);

export const fetchPaymentsByUserId = createAsyncThunk(
  "payment/fetchByUserId",
  async (userId, thunkAPI) => {
    try {
      return await paymentsService.getPaymentsByUserId(userId);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch payments by user.");
    }
  }
);

export const fetchDepositsByPos = createAsyncThunk(
  "payment/fetchDepositsByPos",
  async (posId, thunkAPI) => {
    try {
      return await paymentsService.getDepositsByPos(posId);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch deposits by POS.");
    }
  }
);

export const fetchPaymentsByDateRange = createAsyncThunk(
  "payment/fetchByDateRange",
  async ({ startDate, endDate }, thunkAPI) => {
    try {
      return await paymentsService.getPaymentsByDateRange({ startDate, endDate });
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch payments by date range.");
    }
  }
);

export const addPayment = createAsyncThunk(
  "payment/add",
  async (payment, thunkAPI) => {
    try {
      return await paymentsService.createPayment(payment);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to create payment.");
    }
  }
);

export const editPayment = createAsyncThunk(
  "payment/edit",
  async ({ id, payment }, thunkAPI) => {
    try {
      await paymentsService.updatePayment(id, payment);
      return { id, payment };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update payment.");
    }
  }
);

export const removePayment = createAsyncThunk(
  "payment/remove",
  async (id, thunkAPI) => {
    try {
      await paymentsService.deletePayment(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete payment.");
    }
  }
);

export const removePaymentHard = createAsyncThunk(
  "payment/removeHard",
  async (id, thunkAPI) => {
    try {
      await paymentsService.hardDeletePayment(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to hard delete payment.");
    }
  }
);

export const refundPayment = createAsyncThunk(
  "payment/refund",
  async (id, thunkAPI) => {
    try {
      await paymentsService.refundPayment(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to refund payment.");
    }
  }
);

export const replacePayment = createAsyncThunk(
  "payment/replace",
  async ({ entityType, entityId, payments }, thunkAPI) => {
    try {
      await paymentsService.replacePayments({ entityType, entityId, payments });
      return entityId;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to replace payment.");
    }
  }
);

export const settleFiscalStampByPaymentThunk = createAsyncThunk(
  'payment/settleFiscalStamp',
  async ({ invoiceId, paymentMethodId, note }, thunkAPI) => {
    try {
      const payload = {
        invoiceId,
        paymentMethodId,
        paymentAmountIncludingTax: 1,
        totalAmountToPayIncludingTax: 1,
        remainingAmountIncludingTax: 0,
        paymentIsActive: true,
        paymentIsPayedByPos: false,
        paymentIsADeposit: false,
        paymentDate: new Date().toISOString(),
        paymentNotice: `Solde du timbre fiscal${note?.trim() ? ` : ${note.trim()}` : ''}`,
      };

      return await paymentsService.createPayment(payload);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err?.response?.data || 'Erreur lors du solde du timbre fiscal.'
      );
    }
  }
);

export const settleFiscalStampBulkByPaymentThunk = createAsyncThunk(
  'payment/settleFiscalStampBulk',
  async ({ invoiceIds, paymentMethodId, note }, thunkAPI) => {
    try {
      const payload = {
        invoiceIds,
        paymentMethodId,
        paymentAmountIncludingTax: 1,
        totalAmountToPayIncludingTax: 1,
        remainingAmountIncludingTax: 0,
        paymentIsActive: true,
        paymentIsPayedByPos: false,
        paymentIsADeposit: false,
        paymentDate: new Date().toISOString(),
        paymentNotice: `Solde du timbre fiscal${note?.trim() ? ` : ${note.trim()}` : ''}`,
      };

      return await paymentsService.createBulkPayments(payload);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err?.response?.data || 'Erreur lors du solde multiple du timbre fiscal.'
      );
    }
  }
);

const initialState = {
  payments: [],
  totalCount: 0,
  payment: null,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
};

const setListState = (state, action) => {
  state.loading = false;
  state.payments = action.payload?.items || [];
  state.totalCount = action.payload?.totalCount || 0;
};

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    openModal: (state) => { state.showModal = true; },
    closeModal: (state) => { state.showModal = false; },
    openDeleteModal: (state) => { state.showDeleteModal = true; },
    closeDeleteModal: (state) => { state.showDeleteModal = false; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, setListState)
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchActivePayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivePayments.fulfilled, setListState)
      .addCase(fetchActivePayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchInactivePayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInactivePayments.fulfilled, setListState)
      .addCase(fetchInactivePayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchPaymentsByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentsByUserId.fulfilled, setListState)
      .addCase(fetchPaymentsByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchDepositsByPos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepositsByPos.fulfilled, setListState)
      .addCase(fetchDepositsByPos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchPaymentsByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentsByDateRange.fulfilled, setListState)
      .addCase(fetchPaymentsByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.payment = action.payload;
      })

      .addCase(addPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.showModal = false;
        state.payments = [action.payload, ...state.payments];
        state.totalCount += 1;
      })
      .addCase(addPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(settleFiscalStampByPaymentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(settleFiscalStampByPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = [action.payload, ...state.payments];
        state.totalCount += 1;
      })
      .addCase(settleFiscalStampByPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(settleFiscalStampBulkByPaymentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(settleFiscalStampBulkByPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;

        const createdPayments = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];

        state.payments = [...createdPayments, ...state.payments];
        state.totalCount += createdPayments.length;
      })
      .addCase(settleFiscalStampBulkByPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editPayment.fulfilled, (state, action) => {
        const { id, payment } = action.payload;
        state.loading = false;
        state.showModal = false;
        state.payments = state.payments.map((p) =>
          p.paymentId === id ? { ...p, ...payment, paymentId: id } : p
        );
      })
      .addCase(editPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.showDeleteModal = false;
        state.payments = state.payments.filter((p) => p.paymentId !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(removePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(replacePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(replacePayment.fulfilled, (state) => {
        state.loading = false;
        state.showModal = false;
      })
      .addCase(replacePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(refundPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refundPayment.fulfilled, (state, action) => {
        state.loading = false;
        const refundedId = action.payload;

        state.payments = state.payments.map((p) =>
          p.paymentId === refundedId
            ? { ...p, statusName: 'Paiement remboursé' }
            : p
        );
      })
      .addCase(refundPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      ;
  },
});

export const { openModal, closeModal, openDeleteModal, closeDeleteModal } = paymentSlice.actions;
export default paymentSlice.reducer;